import React, { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { auth, db } from '../firebase/config';
import { prefersPersistentLogin } from '../utils/deviceEnv';
import { useNavigate } from 'react-router-dom';
import { RoofBeamWordmark } from '../components/RoofBeamLogo';

// Native always runs on indexedDBLocalPersistence (set once in firebase/config.js) —
// there's no session-only mode in a single-app WebView, so the "Remember me"
// choice has nothing to do and is hidden rather than shown as a no-op.
const IS_NATIVE = Capacitor.isNativePlatform();

// Web, but a phone browser or an installed home-screen PWA: sessionStorage
// (what browserSessionPersistence uses) doesn't survive the app being
// swiped away and reopened in either context — see utils/deviceEnv.js.
// Forced to persistent regardless of "Remember me"; the checkbox is hidden
// here the same way it's hidden on native, since unchecking it would be a
// no-op. Only a genuine desktop browser gets a real session-only choice.
const FORCE_WEB_PERSISTENT = !IS_NATIVE && prefersPersistentLogin();

function EyeIcon({ open }) {
  return open ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export function roleDestination(claims) {
  if (claims.role === 'owner')          return '/dashboard';
  if (claims.role === 'crew')          return '/crew';
  if (claims.role === 'sales_rep')     return '/canvass-home';
  if (claims.role === 'sales_manager') return '/manager';
  if (claims.role === 'guest')         return '/g';   // Slice 2A — guests never reach the app shell
  if (claims.role === 'research_bot')  return '/research'; // its only allowed route (routeRoles.js) — was falling through to /dashboard, which it's denied from
  // BUG FIX (follow-up #2): this used to fall through to '/dashboard' for
  // ANY unrecognized/missing role (including a genuinely unprovisioned,
  // no-claim account) — but routeRoles.js's Phase C cutover denies
  // '/dashboard' to a null role same as every other route, so
  // PrivateRoute.js would redirect right back to this same function's
  // result, an infinite self-redirect that never mounts a real page (the
  // reported "redirected to /dashboard, black screen" bug). '/login' is
  // the one destination that's never PrivateRoute-guarded, so it can't
  // loop; routeAfterAuth (above) is what actually signs a null-role
  // account back out with a clear message once it lands there.
  return '/login';
}

// Deactivated sales accounts (agreement declined, or owner-toggled inactive)
// are refused at the door. Returns true when routing proceeded.
async function routeAfterAuth(user, navigate, setError) {
  const result = await user.getIdTokenResult();
  const role = result.claims.role || null;
  // BUG FIX (follow-up #2): a signed-in account with NO role claim at all
  // used to fall through roleDestination's final `return '/dashboard'` —
  // but routeRoles.js's Phase C cutover made '/dashboard' (like every other
  // route) deny a null role, so PrivateRoute immediately redirected them
  // right back to roleDestination(claims), which is '/dashboard' again:
  // an infinite self-redirect with the real page never mounting — the
  // "redirected to /dashboard, black screen forever" bug. An owner MUST
  // carry the explicit 'owner' claim in this phase (see routeRoles.js's own
  // header comment), so a null role here is a genuinely unprovisioned
  // account, not an owner — same remediation as the inactive-account case
  // directly below: sign out with a clear, actionable message instead of
  // routing anywhere inside the app.
  if (!role) {
    await signOut(auth);
    setError('Your account has no role assigned yet. Contact your administrator.');
    return false;
  }
  if (role === 'sales_rep' || role === 'sales_manager') {
    try {
      const snap = await getDoc(doc(db, 'salesTeam', user.uid));
      if (snap.exists() && snap.data().active === false) {
        await signOut(auth);
        setError('Your account is inactive. Contact your administrator.');
        return false;
      }
    } catch { /* profile unreadable — AgreementGate still guards the app */ }
  }
  navigate(roleDestination(result.claims), { replace: true });
  return true;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (user) {
        await routeAfterAuth(user, navigate, setError);
      }
    });
    return unsub;
  }, [navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!IS_NATIVE) {
        const persistent = FORCE_WEB_PERSISTENT || rememberMe;
        await setPersistence(auth, persistent ? browserLocalPersistence : browserSessionPersistence);
      }
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await routeAfterAuth(cred.user, navigate, setError);
    } catch (err) {
      setError(err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found'
        ? 'Invalid email or password.'
        : err.message);
    }
    setLoading(false);
  }

  return (
    <div className="rb-login">
      {/* Subtle landing-page echo: faint grid + two soft glows, calm */}
      <span className="rb-login-glow" style={{ top: '18%', left: '12%', background: 'rgba(231,76,60,0.14)' }} />
      <span className="rb-login-glow" style={{ bottom: '14%', right: '10%', background: 'rgba(243,156,18,0.10)' }} />

      <div className="rb-login-inner">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--sp-5)' }}>
          <RoofBeamWordmark size={44} />
        </div>

        <div className="rb-login-card">
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 'var(--sp-4)' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Email</label>
              <input
                className="rb-login-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div style={{ marginBottom: 'var(--sp-4)' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="rb-login-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', alignItems: 'center', transition: 'color var(--transition-fast)' }}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            {!IS_NATIVE && !FORCE_WEB_PERSISTENT && (
              <div style={{ marginBottom: 'var(--sp-5)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  id="rememberMe"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--red-light)', cursor: 'pointer' }}
                />
                <label htmlFor="rememberMe" style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)', cursor: 'pointer', userSelect: 'none' }}>
                  Remember me
                </label>
              </div>
            )}

            {error && (
              <div className="rb-login-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', minHeight: 48, fontSize: 'var(--fs-md)', borderRadius: 'var(--radius-md)', opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: 'var(--sp-5)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          RoofBeam™ · a KLO Roofing company
        </div>
      </div>

      <style>{`
        .rb-login {
          min-height: 100vh;
          display: flex; align-items: center; justify-content: center;
          padding: var(--sp-5) var(--sp-4);
          position: relative; overflow: hidden;
          background:
            repeating-linear-gradient(0deg, transparent, transparent 47px, rgba(255,255,255,0.025) 48px),
            repeating-linear-gradient(90deg, transparent, transparent 47px, rgba(255,255,255,0.025) 48px),
            #111;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .rb-login-glow {
          position: absolute; width: 220px; height: 220px; border-radius: 50%;
          filter: blur(70px); pointer-events: none;
        }
        .rb-login-inner {
          width: 100%; max-width: 400px; position: relative;
          animation: rbLoginRise var(--transition-normal) ease both;
        }
        @keyframes rbLoginRise {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rb-login-card {
          background: var(--dark2);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-raised);
          padding: clamp(24px, 6vw, 40px);
        }
        .rb-login-input {
          width: 100%; box-sizing: border-box;
          min-height: 46px; padding: 12px 14px;
          background: rgba(255,255,255,0.06);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          color: var(--text);
          font-size: 16px; /* prevents iOS focus zoom on all viewports */
          outline: none;
          transition: border-color var(--transition-fast), box-shadow var(--transition-fast), background var(--transition-fast);
        }
        .rb-login-input:focus {
          border-color: var(--red-light);
          box-shadow: 0 0 0 3px rgba(231,76,60,0.18);
          background: rgba(255,255,255,0.08);
        }
        .rb-login-error {
          background: rgba(231,76,60,0.12);
          border: 1px solid rgba(231,76,60,0.35);
          border-radius: var(--radius-sm);
          padding: 10px 12px;
          margin-bottom: var(--sp-4);
          color: #ff9d94;
          font-size: var(--fs-sm);
          line-height: 1.5;
          text-align: center;
        }
      `}</style>
    </div>
  );
}
